// The assassination order on an assassin's row (module `espionage.ops`): one
// button per royal the sim says is within reach.
//
// The reach is the sim's answer (`royalTargets`, the same test the order is
// refused by), so the list never offers a hit the sim would turn down.

import type { PlayerHudEntry } from '@aod/ui';
import { useState } from 'react';
import type { EspionageOps, Spy } from '../../espionage/espionage.js';
import { useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import { FramePicker } from './FramePicker.js';

export interface RoyalHitControlsProps {
  readonly ops: EspionageOps;
  readonly spy: Spy;
  readonly players: readonly PlayerHudEntry[];
  readonly localPlayerIndex: number;
  /** How to call a royal in words ("Prince of House X"). */
  readonly nameOf: (royalId: number) => string;
  readonly onAssassinate: (unitId: string, royalId: number, frameSeat?: number) => boolean;
}

export function RoyalHitControls({
  ops,
  spy,
  players,
  localPlayerIndex,
  nameOf,
  onAssassinate,
}: RoyalHitControlsProps) {
  const t = useT();
  const [frame, setFrame] = useState<number | null>(null);
  const [refused, setRefused] = useState(false);
  const targets = spy.royalTargets ?? [];
  if (!ops.assassinate || targets.length === 0) return null;
  const courts = new Set(targets.map((r) => r.ownerId));
  // One picker serves every royal: only offer houses that are none of the courts struck.
  const shared = players.filter((p) => p.playerId !== localPlayerIndex && !courts.has(p.playerId));
  return (
    <div className="flex flex-col gap-1" data-testid={`spy-royals-${spy.unitId}`}>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {t('espionage.royalTargets')}
      </span>
      <FramePicker
        testId={`spy-royals-frame-${spy.unitId}`}
        falseFlag={ops.falseFlag}
        suspects={shared}
        value={frame}
        onChange={setFrame}
      />
      {targets.map((royal) => (
        <button
          key={royal.royalId}
          type="button"
          data-testid={`spy-assassinate-${spy.unitId}-${royal.royalId}`}
          {...appDom({
            id: `spy-assassinate-${spy.unitId}-${royal.royalId}`,
            type: 'Button',
            role: 'button',
            scope: 'section',
          })}
          onClick={() => {
            const ok = onAssassinate(spy.unitId, royal.royalId, frame === null ? undefined : frame);
            setRefused(!ok);
          }}
          className="rounded border border-border/60 px-2 py-0.5 text-left text-[10px] hover:bg-muted/60"
        >
          {t('espionage.assassinate', { royal: nameOf(royal.royalId), gold: ops.assassinGold })}
        </button>
      ))}
      {refused && (
        <output className="text-[10px] text-destructive">
          {t('espionage.assassinateRefused')}
        </output>
      )}
    </div>
  );
}
